import { useCallback } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import type {
  BreakdownLineItem,
  BreakdownLineItemWithSubItems,
  BreakdownSubItemInput,
  PatchBreakdownInput,
} from '@jobos/shared';
import { breakdownKeys, type usePatchBreakdown } from '@/hooks/useBreakdown';
import { useAutosave, type UseAutosaveReturn } from '@/hooks/useAutosave';
import type { useWriteBreakdownDocumentContents } from '@/hooks/useBreakdownDocuments';
import { buildDocContents } from './buildDocContents';

// Structural shape shared by useEnsureCarpetLineItem / useEnsureEquipmentLineItem.
// Captures exactly the members this hook consumes: the mutateAsync used to lazily
// create the slot's line item, and isPending for the composed isSaving flag.
export interface EnsureLineItem {
  mutateAsync: (jobId: string) => Promise<{ item: BreakdownLineItem }>;
  isPending: boolean;
}

export interface UseSingleCategorySaveOptions {
  jobId: string;
  lineItem: BreakdownLineItemWithSubItems | null;
  ensureLineItem: EnsureLineItem;
  patchBreakdown: ReturnType<typeof usePatchBreakdown>;
  queryClient: QueryClient;
  isOnline: boolean;
  writeDocContents: ReturnType<typeof useWriteBreakdownDocumentContents>;
  rates: { taxRate: number; markupRate: number; roundingPrecision: number | null };
  documentId?: string;
  /** Line-item category written in the doc path (e.g. 'Carpet Cleaning', 'Equipment Rental'). */
  category: string;
  /** Offline-storage key builder for the slot (offlineCarpetKey | offlineEquipmentKey). */
  offlineKey: (jobId: string) => string;
  /** Backup discriminator, used for both the autosave backupKind and the downloadBackup envelope kind. */
  backupKind: 'carpet' | 'equipment';
}

// Single-category save = autosave with a specialty-specific saveFn.
// Exposed fields mirror the UseAutosaveReturn plus a few renamed aliases that
// match the view's call sites.
export interface UseSingleCategorySaveReturn
  extends Omit<UseAutosaveReturn<BreakdownSubItemInput[]>, 'pending' | 'downloadBackup'> {
  pendingSubItems: BreakdownSubItemInput[] | null;
  downloadBackup: (jobCode: string) => void;
}

export function useSingleCategorySave({
  jobId,
  lineItem,
  ensureLineItem,
  patchBreakdown,
  queryClient,
  isOnline,
  writeDocContents,
  rates,
  documentId,
  category,
  offlineKey,
  backupKind,
}: UseSingleCategorySaveOptions): UseSingleCategorySaveReturn {
  const saveFn = useCallback(
    async (pending: BreakdownSubItemInput[]) => {
      if (documentId) {
        // Doc path: replace-all the document's single configured-category line item.
        // INVARIANT: a single-category-slot document only ever holds this one loose
        // line item (the configured category). The editor reads exactly that item,
        // and the contents endpoint is delete-all-then-insert — so we write only the
        // one item. This is safe because single-category jobs never route through the
        // generic multi-item editor and Save-As/Duplicate can only copy from another
        // (single-item) single-category-slot doc. If that ever changes, this would
        // silently drop the other rows; in that case, seed `lineItems` from the loaded
        // doc and replace only the matched item.
        const contents = buildDocContents({
          taxRate: rates.taxRate,
          markupRate: rates.markupRate,
          roundingPrecision: rates.roundingPrecision,
          lineItems: [
            {
              id: lineItem?.id ?? crypto.randomUUID(),
              category,
              subItems: pending.map((sub) => ({
                id: crypto.randomUUID(),
                type: sub.type,
                hours: sub.hours ?? null,
                rate: sub.rate ?? null,
                amount: sub.amount ?? null,
                note: sub.note ?? null,
              })),
            },
          ],
        });
        await writeDocContents.mutateAsync({ docId: documentId, contents });
        await queryClient.invalidateQueries({ queryKey: [...breakdownKeys.detail(jobId), documentId] });
        return;
      }

      // Legacy path (flag off): existing patch logic, UNCHANGED.
      let li = lineItem;
      if (!li) {
        const { item } = await ensureLineItem.mutateAsync(jobId);
        li = { ...item, subItems: [] };
      }

      const patches: PatchBreakdownInput['patches'] = [];
      for (const sub of li.subItems) {
        patches.push({ op: 'deleteSubItem', id: sub.id, lineItemId: li.id });
      }
      for (const [i, sub] of pending.entries()) {
        patches.push({
          op: 'createSubItem',
          id: crypto.randomUUID(),
          lineItemId: li.id,
          data: {
            type: sub.type,
            ...(sub.hours !== undefined ? { hours: sub.hours } : {}),
            ...(sub.rate !== undefined ? { rate: sub.rate } : {}),
            ...(sub.amount !== undefined ? { amount: sub.amount } : {}),
            ...(sub.note !== undefined ? { note: sub.note } : {}),
            sortOrder: i,
          },
        });
      }

      await patchBreakdown.mutateAsync({ patches });
      await queryClient.invalidateQueries({ queryKey: breakdownKeys.detail(jobId) });
    },
    [documentId, rates, lineItem, ensureLineItem, jobId, patchBreakdown, writeDocContents, queryClient, category],
  );

  const autosave = useAutosave<BreakdownSubItemInput[]>({
    offlineKey: offlineKey(jobId),
    isOnline,
    saveFn,
    backupKind,
  });

  const downloadBackup = useCallback(
    (jobCode: string) =>
      autosave.downloadBackup(jobCode, (value) => ({
        version: 1,
        kind: backupKind,
        exportedAt: new Date().toISOString(),
        jobId,
        jobCode,
        subItems: value ?? [],
      })),
    [autosave, jobId, backupKind],
  );

  return {
    ...autosave,
    // Expose React Query's isSaving OR the autosave's internal save-in-flight
    // state so the UI reflects both manual mutations (ensureLineItem, patch)
    // and autosave ticks.
    isSaving: autosave.isSaving || patchBreakdown.isPending || ensureLineItem.isPending || writeDocContents.isPending,
    pendingSubItems: autosave.pending,
    downloadBackup,
  };
}
